import { useState, useEffect } from "react";
import styles from "./MobileSidebar.module.css";
import Link from "next/link";
import { useRouter } from "next/router";
import { capitalizeText, handleLinkClick } from "@/Config/helper";
import UnifiedLoginUser from "../UnifiedLoginUser/UnifiedLoginUser";

import {
  X,
  Menu,
  ChevronRight,
  ChevronDown,
  ArrowLeftCircle,
  Dot,
} from "lucide-react";

interface NavItem {
  id: number;
  label: string;
  url: string;
  links: Link[];
  nested_list_links: NestedListLink[];
}

interface Link {
  id: number;
  label: string;
  url: string;
}

interface NestedListLink {
  id: number;
  title: string;
  url: string;
  links: Link[];
  lists: ListItem[];
}

interface ListItem {
  id: number;
  title: string;
  url: string;
  items: Item[];
}

interface Item {
  id: number;
  label: string;
  url: string;
}

interface Props {
  apiData: NavItem[];
  // handleEnrollButtonClick: () => void;
  handleBroutcherButtonClick: (broutcherLink?: string) => void;
  data?: any;
  feesBrochure?: any;
  prospectusBrochure: any;
}

const MobileSidebar: React.FC<Props> = ({
  apiData,
  data,
  // handleEnrollButtonClick,
  handleBroutcherButtonClick,
  feesBrochure,
  prospectusBrochure,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isStudyExpanded, setIsStudyExpanded] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [expandedDropdown, setExpandedDropdown] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const router = useRouter(); // Use the router to detect route changes

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    setIsStudyExpanded(false);
  };

  const toggleStudyExpansion = () => {
    setIsStudyExpanded(true);
    setExpandedCategory(null);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const handleBackToMain = () => {
    setIsStudyExpanded(false);
  };

  const toggleDropdown = (id: number) => {
    setExpandedDropdown(expandedDropdown === id ? null : id);
  };

  // Close the sidebar when the route changes
  useEffect(() => {
    const handleRouteChange = () => {
      setIsSidebarOpen(false); // Close the sidebar when a route change occurs
    };

    router.events.on("routeChangeStart", handleRouteChange);

    // Clean up the event listener
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events]);
  const navLinks = data?.nav_links || [];

  return (
    <div>
      <button
        aria-labelledby="HambergerMenu"
        title="hamberger"
        id="hambergerm"
        className={styles.hamburger}
        onClick={toggleSidebar}
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {isSidebarOpen && (
        <div className={styles.sidebar}>
          {!isStudyExpanded ? (
            <>
              <ul>
                {apiData?.map((section: NavItem) => (
                  <li key={section?.id} className={styles.menuItem}>
                    {section?.label === "Study at Regenesys" ? (
                      <div
                        onClick={toggleStudyExpansion}
                        className={styles.expandable}
                      >
                        <Link
                          href={section?.url}
                          title={section?.label}
                          onClick={(e) =>
                            handleLinkClick(e, section?.url, router)
                          }
                        >
                          {section?.label}
                        </Link>
                        <ChevronRight />
                      </div>
                    ) : (
                      <div>
                        <span>{section?.label}</span>
                        {section?.nested_list_links?.length > 0 && (
                          <span
                            onClick={() => toggleDropdown(section.id)} // Pass section ID
                            style={{ cursor: "pointer" }}
                          >
                            {expandedDropdown === section?.id ? (
                              <ChevronDown />
                            ) : (
                              <ChevronRight />
                            )}
                          </span>
                        )}
                      </div>
                    )}

                    {expandedDropdown === section?.id && (
                      <ul className={styles.dropDownList}>
                        {section?.nested_list_links.map((nestedLink) => (
                          <li key={nestedLink.id}>
                            {nestedLink.title === "Fees" ||
                            nestedLink.title === "Prospectus" ? (
                              <span
                                onClick={() => {
                                  handleBroutcherButtonClick(
                                    nestedLink.title === "Fees"
                                      ? feesBrochure
                                      : prospectusBrochure
                                  );
                                }}
                                className={styles.submenuTitleButton}
                              >
                                <Dot />
                                {nestedLink?.title}
                              </span>
                            ) : (
                              <Link
                                href={nestedLink?.url}
                                className={styles.submenuTitle}
                                title={nestedLink?.title}
                                onClick={(e) =>
                                  handleLinkClick(e, nestedLink?.url, router)
                                }
                              >
                                <Dot />
                                {nestedLink?.title}
                              </Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
                {navLinks?.map((link: any) => {
                  const shouldOpenInNewTab =
                    link?.url ===
                      "https://regenius.regenesys.net/login/index.php" ||
                    link?.url === "https://www.regenesys.net/reginsights";

                  return (
                    <li key={link.id} className={styles.menuItem}>
                      <Link
                        key={link?.id}
                        href={link?.url}
                        className={styles.navLink}
                        target={shouldOpenInNewTab ? "_blank" : undefined}
                        title={link?.label}
                        rel={
                          shouldOpenInNewTab ? "noopener noreferrer" : undefined
                        }
                        onClick={(e) => handleLinkClick(e, link?.url, router)}
                      >
                        {link?.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* <button
                className={styles.applyButton}
                onClick={handleEnrollButtonClick}
                data-interaction="Nav CTA Click"
              >
                <MdLogin />
                <Link
                  href={
                    apiData.find(
                      (section: NavItem) => section?.label === "Apply"
                    )?.url || "/"
                  }
                >
                  Apply
                </Link>
              </button> */}
            </>
          ) : (
            <>
              <div>
                <div onClick={handleBackToMain} className={styles.backButton}>
                  <ArrowLeftCircle /> Study at Regenesys
                </div>
                <ul className={styles.subMenu}>
                  {apiData[0]?.nested_list_links.map(
                    (category: NestedListLink) => (
                      <li key={category?.title} className={styles.categoryItem}>
                        <div className={styles.categoryHeader}>
                          <div className={styles.categoryHeaderBorder}>
                            <span
                              style={{
                                display:
                                  expandedCategory === category?.title
                                    ? "block"
                                    : "none",
                              }}
                            ></span>
                            <Link
                              href={category?.url}
                              title={category?.title}
                              style={{
                                fontWeight:
                                  expandedCategory === category?.title
                                    ? "600"
                                    : "400",
                                color:
                                  expandedCategory === category?.title
                                    ? "#18463E"
                                    : "",
                              }}
                            >
                              {capitalizeText(category?.title)}
                            </Link>
                          </div>
                          <span
                            onClick={() => toggleCategory(category?.title)}
                            className={styles.arrowIcon}
                          >
                            {expandedCategory === category?.title ? (
                              <ChevronDown />
                            ) : (
                              <ChevronRight />
                            )}
                          </span>
                        </div>
                        {expandedCategory === category?.title && (
                          <ul className={styles.subCategory}>
                            {category?.lists?.map((section: ListItem) => (
                              <li key={section.title}>
                                <strong>
                                  <Link
                                    href={section?.url}
                                    title={section?.title}
                                  >
                                    {section?.title}
                                  </Link>
                                </strong>
                                <ul>
                                  {section.items.map((item: Item) => (
                                    <li
                                      key={item?.label}
                                      className={styles.programItem}
                                    >
                                      <Dot />
                                      <Link
                                        href={item?.url}
                                        title={item?.label}
                                      >
                                        {item?.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            ))}
                            {category.links?.map((linkItem: Link) => (
                              <li key={linkItem.label}>
                                <Dot />
                                <Link
                                  href={linkItem?.url}
                                  className={styles.bottomLink}
                                  title={linkItem?.label}
                                >
                                  {linkItem?.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </>
          )}
          <div className={styles.loginText} onClick={handleOpenModal}>
            Login
          </div>
        </div>
      )}
      <UnifiedLoginUser isOpen={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
};

export default MobileSidebar;
